import { formatWind, type Observation } from '@skyweather/weather-core'
import { windUnit } from './format.js'
import { RecentGusts } from './RecentGusts.js'
import type { SiteUnits } from './types.js'
import { bandsOut, downwindBearing, polar, sockGeometry } from './windsock.js'

interface Props {
  observation: Observation | null
  /** The shown feed's recent readings, for the recent peak gusts list. */
  recent: Observation[]
  now: number
  units: SiteUnits
  /** The feed is not reporting: the sock shows its last reading, greyed. */
  stale: boolean
  /** The owner's switches: the windsock itself and the recent-gusts list. Default: both on. */
  showSock?: boolean
  showGusts?: boolean
}

const SIZE = 280
const C = SIZE / 2
const RING = 118

/**
 * Beside the feed: the wind in more detail. A top-down windsock pointing the way the wind blows, held out
 * as far as this wind would hold it (3 kt a band, full at 15 kt), with the gust as a faint outline, and the
 * wind direction in degrees. (The owner's limits are dots above the left wind reading's direction, in SourcePanel.)
 */
export function WindPanel({ observation: o, recent, now, units, stale, showSock = true, showGusts = true }: Props) {
  const kt = o?.windKt ?? null
  const dir = o && !o.windVariable ? o.windDirDeg : null
  const calm = kt === 0 && (o?.gustKt === null || o?.gustKt === 0)
  const variableRange = o && o.windVarFromDeg !== null && o.windVarToDeg !== null ? [o.windVarFromDeg, o.windVarToDeg] as const : null
  // A variable wind with a range points the sock at the middle of the range, drawn dashed.
  const pointing = dir ?? (variableRange ? midBearing(variableRange[0], variableRange[1]) : null)
  const sock = pointing !== null && !calm ? sockGeometry(C, C, downwindBearing(pointing), kt) : null
  const gustSock = pointing !== null && o?.gustKt != null && o.gustKt > (kt ?? 0) ? sockGeometry(C, C, downwindBearing(pointing), o.gustKt) : null
  const unit = windUnit(units)

  const label = o === null || kt === null ? 'No wind reading'
    : calm ? 'Calm'
    : dir !== null ? `${String(dir).padStart(3, '0')}°`
    : 'Variable'

  return (
    <aside className={`windpanel${stale ? ' windpanel--stale' : ''}`} aria-label="Wind in detail">
      {showSock && <div className="windpanel__top">
      <svg viewBox={`0 0 ${SIZE} ${SIZE}`} className="sock" role="img" aria-label={sockDescription(label, kt, o?.gustKt ?? null, unit)}>
        <circle cx={C} cy={C} r={RING} className="sock__ring" />
        {[0, 90, 180, 270].map((b) => {
          const p = polar(C, C, b, RING + 13)
          return <text key={b} x={p.x} y={p.y} className="sock__cardinal" textAnchor="middle" dominantBaseline="central">{['N', 'E', 'S', 'W'][b / 90]}</text>
        })}
        {Array.from({ length: 36 }, (_, i) => i * 10).map((b) => {
          const a = polar(C, C, b, RING)
          const z = polar(C, C, b, RING - (b % 30 === 0 ? 10 : 5))
          return <line key={b} x1={a.x} y1={a.y} x2={z.x} y2={z.y} className="sock__tick" />
        })}
        {variableRange && <path d={arc(variableRange[0], variableRange[1])} className="sock__vrange" />}
        {gustSock && gustSock.bands.map((band, i) => <polygon key={`g${i}`} points={band.points} className="sock__gust" />)}
        {sock && sock.bands.map((band, i) => (
          <polygon key={i} points={band.points} className={`sock__band sock__band--${band.colour}${band.out ? '' : ' sock__band--limp'}${dir === null ? ' sock__band--variable' : ''}`} />
        ))}
        {pointing !== null && !calm && (() => {
          // A small arrow on the ring where the wind comes FROM, pointing in.
          const tip = polar(C, C, pointing, RING - 16)
          const l = polar(C, C, pointing - 5, RING + 2)
          const r = polar(C, C, pointing + 5, RING + 2)
          return <polygon points={`${tip.x},${tip.y} ${l.x},${l.y} ${r.x},${r.y}`} className="sock__from" />
        })()}
        <circle cx={C} cy={C} r={calm ? 13 : 5} className={calm ? 'sock__mouth' : 'sock__pole'} />
      </svg>

      <div className="winddeg">
        <span className="winddeg__value">{label}</span>
        {o !== null && kt !== null && !calm && (
          <span className="winddeg__speed">
            {formatWind(kt, unit, { withKnots: false })}
            {o.gustKt !== null ? ` · gusts ${formatWind(o.gustKt, unit, { withKnots: false, role: 'gust' })}` : ''}
          </span>
        )}
        {kt !== null && !calm && (
          <span className="winddeg__sock">{bandsOut(kt) >= 5 ? 'Sock full out' : `Sock ${Math.floor(bandsOut(kt))} of 5 bands out`}</span>
        )}
      </div>
      </div>}

      {showGusts && <RecentGusts readings={recent} units={units} now={now} />}
    </aside>
  )
}

function midBearing(a: number, b: number): number {
  const span = (b - a + 360) % 360
  return (a + span / 2) % 360
}

function arc(from: number, to: number): string {
  const r = RING - 4
  const a = polar(C, C, from, r)
  const b = polar(C, C, to, r)
  const span = (to - from + 360) % 360
  return `M ${a.x} ${a.y} A ${r} ${r} 0 ${span > 180 ? 1 : 0} 1 ${b.x} ${b.y}`
}

function sockDescription(label: string, kt: number | null, gust: number | null, unit: ReturnType<typeof windUnit>): string {
  if (kt === null) return 'Windsock: no wind reading'
  if (label === 'Calm') return 'Windsock hanging limp: calm'
  return `Windsock: wind from ${label}, ${formatWind(kt, unit, { withKnots: false })}${gust !== null ? `, gusting ${formatWind(gust, unit, { withKnots: false, role: 'gust' })}` : ''}`
}
